import React from 'react';
import { Box } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { ChatSourceNote } from './ChatSourceNote';

export interface ChatDefinitionsNoteItem {
  name: string;
  /**
   * `official` — from a dashboard or entered by an admin, marked with a check.
   * `saved` — saved to the knowledge base from a chat.
   * `this_chat` — clarified in this chat only.
   */
  kind: 'official' | 'saved' | 'this_chat';
  /** Where it is defined, e.g. the dashboard's name. */
  where?: string | null;
  /**
   * For a measure: `dashboard` — the figure is the dashboard's own;
   * `own_query` — Copilot worked it out itself, so it may differ from the
   * dashboard. Such an item loses its check mark.
   */
  applied?: 'dashboard' | 'own_query' | null;
}

function suffixFor(item: ChatDefinitionsNoteItem): string | null {
  if (item.kind === 'this_chat') return 'from this chat';
  if (item.applied === 'own_query') {
    return item.where
      ? `worked out by Copilot, not the ${item.where} figure — may differ`
      : 'worked out by Copilot — may differ from the dashboard';
  }
  if (item.applied === 'dashboard' && item.where) return `${item.where} figure`;
  return item.where ?? null;
}

export interface ChatDefinitionsNoteProps {
  items: ChatDefinitionsNoteItem[];
}

/**
 * The quiet line under an answer naming the definitions it relied on:
 * "Definitions used: MQL to CAS (Sales Scorecard) · Sales Lead · Paid (from
 * this chat)". Official ones carry a check mark, unless Copilot worked the
 * figure out itself — then the line says so instead.
 *
 * A `ChatSourceNote` with no trail to open. For saving or changing a
 * definition, use `DefinitionCard`.
 */
export function ChatDefinitionsNote({ items }: ChatDefinitionsNoteProps) {
  if (items.length === 0) return null;
  return (
    <ChatSourceNote>
      Definitions used:{' '}
      {items.map((item, i) => {
        const suffix = suffixFor(item);
        return (
          <React.Fragment key={`${item.name}-${i}`}>
            {i > 0 && ' · '}
            <Box component="span" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              {item.kind === 'official' && item.applied !== 'own_query' && (
                <CheckCircleOutline
                  aria-label="Official"
                  fontSize="inherit"
                  sx={(t) => ({
                    verticalAlign: '-0.15em',
                    mr: 0.25,
                    color: t.tokens.semantic.success,
                  })}
                />
              )}
              {item.name}
              {suffix ? ` (${suffix})` : ''}
            </Box>
          </React.Fragment>
        );
      })}
    </ChatSourceNote>
  );
}
